/*
 * The action submissions, in module scope so a submission outlives the tab panels that remount per
 * view: a remounted control reattaches to the request in flight, and a sent request stays sent for
 * the session. Slots are per user, tenant and action. Kept free of runtime imports so the sign-out
 * saga can clear it without pulling the Overwatch chunk into the main bundle.
 */
import type { OverwatchActionFailure } from 'api/overwatch';

import type { IOverwatchActionResult } from '../widgets/types';
import type { IActionFailureView } from './overwatchActions';

export interface IActionSubmission {
  status: 'idle' | 'sending' | 'succeeded' | 'failed';
  /** Reused by every resend of the same input; dropped once the input changes. */
  clientRequestId: string | null;
  input: Record<string, unknown> | null;
  result: IOverwatchActionResult | null;
  /** The client's time at the 201, for a 201 that carries no `submitted_at`. */
  sentAt: string | null;
  failure: (IActionFailureView & { kind: OverwatchActionFailure['kind'] }) | null;
  /** A 50332 wait: nothing is resent before this time. */
  retryAt: number | null;
  /** A 403 hides the control for the rest of the page session. */
  hidden: boolean;
  /** A 403 asks the next mounted control to reload discovery. */
  discoveryStale: boolean;
  /** The user has moved on from the failure (the wizard's Back). */
  acknowledged: boolean;
  /** What the caller needs to draw the submission again after a remount (the wizard's draft). */
  context: unknown;
}

export const EMPTY_SUBMISSION: IActionSubmission = {
  status: 'idle',
  clientRequestId: null,
  input: null,
  result: null,
  sentAt: null,
  failure: null,
  retryAt: null,
  hidden: false,
  discoveryStale: false,
  acknowledged: false,
  context: null
};

const submissions = new Map<string, IActionSubmission>();
const listeners = new Set<() => void>();

export const readSubmission = (slot: string) => submissions.get(slot) ?? EMPTY_SUBMISSION;

export const writeSubmission = (slot: string, changes: Partial<IActionSubmission>) => {
  submissions.set(slot, { ...readSubmission(slot), ...changes });
  listeners.forEach(listener => listener());
};

export const subscribeSubmissions = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** Sign-out empties the store, so nothing carries over to the next user. */
export const clearActionSubmissions = () => {
  submissions.clear();
  listeners.forEach(listener => listener());
};
